<template>
  <div>
    <div class="panel-heading order-heading-with-stats">
      <h4 class="panel-title">{{ $t('delivery.deliveryInvoicesTitle') }}</h4>

      <div class="order-heading-right">
        <div class="order-toolbar-buttons">
          <a class="order-toolbar-btn order-toolbar-btn--icon-only" :title="$t('orders.filtersToggleLabel')" @click="toggleFiltersVisible">
            <i class="material-icons">{{ showFilters ? 'expand_less' : 'expand_more' }}</i>
          </a>
          <a class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="reloadTable">
            <i class="material-icons">cached</i>
            <span v-if="showButtonLabels">{{ $t('datatable.reloadShortLabel') }}</span>
          </a>
          <a v-if="$_has_perm('change_delivery_invoice_status')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="openBulkChangeStatusDialog">
            <i class="material-icons" style="color:#6a1b9a">autorenew</i>
            <span v-if="showButtonLabels">{{ $t('datatable.changeStatusButton') }}</span>
          </a>
          <a v-if="$_has_perm('mark_delivery_invoice_received_amount')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="confirmReceiveAll">
            <i class="material-icons" style="color:#1565c0">receipt</i>
            <span v-if="showButtonLabels">{{ $t('datatable.receiveButton') }}</span>
          </a>
          <a v-if="$_has_perm('unmark_delivery_invoice_received_amount')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="confirmUnreceiveAll">
            <i class="material-icons" style="color:#c62828">undo</i>
            <span v-if="showButtonLabels">{{ $t('datatable.unreceiveButton') }}</span>
          </a>
          <a class="order-toolbar-display-toggle" @click="$_toggle_toolbar_button_display()">
            <i class="material-icons">{{ showButtonLabels ? 'label_off' : 'label' }}</i>
          </a>
        </div>

        <!-- Totals reflect the backend footer aggregate (matches the current filter, not just
             the loaded page) -- footer is null until the first getResults() response returns,
             hence the '0' fallback, same convention as orderInvoices.vue's own stat cards. -->
        <div class="order-stats-row order-stats-row--compact">
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--blue"><i class="material-icons">list_alt</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('reports.totalOrders') }}</span>
              <span class="order-stat-value">{{ footer ? footer.total_count : (pagination.total || totalResult) }}</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--green"><i class="material-icons">attach_money</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('printLogs.totalAmountLabel') }}</span>
              <span class="order-stat-value">$ {{ footer ? Number(footer.total_price || 0).toFixed(2) : '0.00' }}</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--orange"><i class="material-icons">local_shipping</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('columns.deliveryPrice') }}</span>
              <span class="order-stat-value">{{ footer ? formatIQD(footer.total_pay_on_delivery_iqd) : 0 }}</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--purple"><i class="material-icons">today</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('adminDashboard.ordersTodayLabel') }}</span>
              <span class="order-stat-value">{{ ordersTodayCount }}</span>
              <span class="order-stat-note">({{ $t('orders.currentPageScopeNote') }})</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="panel-body"
      style="display: block"
    >
      <div class="row">
        <preDatatableComponent
          ref="preDatatable"
          :hideReloadButton="true"
          :collapsibleFilters="true"
          :filtersVisible="showFilters"
          :removeAddButton="true"
          :removeWarehouseDeliverButton="true"
          :tableData="tableData"
          :resetGetResults="resetGetResults"
          :resetGetResults2="resetGetResults2"
          :searchMe="searchMe"
          :enableCustomerSearchSuggestions="true"
          :pagination="pagination"
          :perPageFix="perPageFix"
          :filterTable="filterTable"
          :columns="columns"
          :showHideColumn="showHideColumn"
          :storageKey="'cols_deliveryInvoices_v9'"
          :defaultHideCol="defaultHideCol"
          :resultsVersion="resultsVersion"
          :filterMade="filterMade"
          :totalResult="totalResult"
          :statusFilterItems="statusList"
          :statusFilterChange="handleStatusFilter"
          :showReceivedFilter="true"
          :receivedFilterChange="handleReceivedFilter"
          :receivedFilterItemsOverride="receivedFilterItemsOverride"
          :noWrapFilters="true"
          @branch-edit="branchEdit"
        >
          <template v-slot:custom-filter-before-date>
            <template v-if="showFilters">
            <v-flex
              class="filter-field px-2"
              style="align-self: flex-end;"
            >
              <v-select
                :items="dateFieldFilterItems"
                v-model="tableData.date_field"
                @change="getResults()"
                :label="$t('columns.dateFilterByLabel')"
                single-line
                hide-details
                item-text="text"
                item-value="value"
              ></v-select>
            </v-flex>
            </template>
          </template>
        </preDatatableComponent>

        <v-dialog
          v-model="changeStatusDialog"
          max-width="480"
        >
          <v-card>
            <v-card-title>
              <div class="headline">{{ $t('delivery.changeStatusTooltip') }}</div>
              <div
                v-if="changeStatusBulkMode"
                style="font-size:13px; color:#666; padding-top:2px;"
              >
                {{ $t('delivery.invoiceSelectedCount', { count: selectedIds.length }) }}
              </div>
              <div
                v-else-if="changeStatusRow"
                style="font-size:13px; color:#666; padding-top:2px;"
              >
                {{ $t('invoice.invoiceTooltip') }}: <strong>{{ changeStatusRow.invoice_number }}</strong>
              </div>
            </v-card-title>
            <v-card-text style="padding-top:12px; overflow:visible;">
              <v-select
                v-model="selectedNewStatus"
                :items="statusList"
                item-text="text"
                item-value="value"
                :label="$t('delivery.selectStatusLabel')"
                hide-details
              />
            </v-card-text>
            <v-card-actions style="padding-bottom:12px;">
              <v-spacer></v-spacer>
              <v-btn
                @click="changeStatusDialog = false"
                outline
                style="padding: 0 16px !important;"
              >{{ $t('common.cancel') }}</v-btn>
              <v-btn
                color="primary"
                outline
                :disabled="!selectedNewStatus || changeStatusSaving"
                @click="saveStatusChange()"
                style="padding: 0 16px !important;"
              >{{ $t('common.save') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <div class="col-xs-12">
          <div class="table-responsive oc-table-shell">
          <datatable
            :sortDir="tableData.sortDir"
            :columns="columns"
            :sortByColumn="sortByColumn"
            :searchOneCol="searchOneCol"
            :sortKey="sortKey"
            :sortOrders="sortOrders"
            :checkAll="checkAll"
            :unCheckAll="unCheckAll"
          >
            <tbody>
              <tr
                v-for="r in apiResults"
                :key="r.delivery_invoice_id"
              >
                <td>
                  <input
                    type="checkbox"
                    :checked="isGroupSelected(r)"
                    @change="toggleGroupSelection(r, $event.target.checked)"
                  />
                </td>
                <td class="oc-cell">
                  <div class="oc-order-number">{{ r.invoice_number }}</div>
                  <div class="oc-order-sub" v-if="r.staff_name">{{ $t('columns.staff') }}: {{ r.staff_name }}</div>
                </td>
                <td class="oc-cell ws-nowrap">
                  <span class="customer-name-stack">
                    <span>{{ r.customer_name }}</span>
                    <span v-if="r.phone_number" class="customer-phone-line">{{ r.phone_number }}</span>
                    <span v-if="r.phone_number_2" class="customer-phone-line">{{ r.phone_number_2 }}</span>
                  </span>
                  <open-whatsapp-and-viber :phoneNumber="r.phone_number" />
                  <div class="oc-item-sub customer-note-color" v-if="r.customer_note">{{ r.customer_note }}</div>
                </td>
                <td class="oc-cell oc-invoices-cell">
                  <div
                    v-for="invoice in r.invoices"
                    :key="invoice.delivery_invoice_id"
                    class="d-flex align-center my-1"
                  >
                    <span
                      class="badge badge-primary mr-1"
                      :style="invoice.order_invoice_payment_status ? { backgroundColor: paymentStatusColor(invoice.order_invoice_payment_status).bg, color: paymentStatusColor(invoice.order_invoice_payment_status).color } : {}"
                    >{{ invoice.order_invoice_number || r.invoice_number }}</span>
                    <a
                      v-if="$_has_perm('record_invoice_payment') && invoice.order_invoice_id_fk && invoice.order_invoice_payment_status !== 'paid' && invoice.order_invoice_payment_status !== 'cancel'"
                      :tooltip="$t('invoice.recordPaymentTooltip')"
                      @click="openRecordPaymentModal(invoice, r)"
                    >
                      <i
                        class="fa fa-dollar"
                        style="font-size: 14px; color: #2e7d32"
                      ></i>
                    </a>
                  </div>
                  <div
                    v-if="r.orders && r.orders.length"
                    class="oc-order-badges"
                  >
                    <span
                      v-for="order in r.orders"
                      :key="order.order_id"
                      class="badge badge-primary"
                    >
                      {{ order.theOrderID }}
                    </span>
                  </div>
                </td>
                <td class="combined-cell-td oc-cell">
                  <div class="combined-cell">
                    <div><span class="combined-cell-label">{{ $t('columns.totalPrice') }}:</span> $ {{ Number(r.total_price || 0).toFixed(2) }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.deliveryPrice') }}:</span> {{ formatIQD(r.pay_on_delivery_iqd) }}</div>
                  </div>
                </td>
                <td class="oc-cell">
                  <span
                    class="oc-status-chip"
                    :style="{ background: statusColor(r.status).bg, color: statusColor(r.status).color, cursor: 'pointer' }"
                    :tooltip="$t('delivery.changeStatusTooltip')"
                    @click="openSingleChangeStatusDialog(r)"
                  >{{ statusLabel(r.status) }}</span>
                </td>
                <td class="oc-cell">
                  <span
                    v-if="r.amount_received_at"
                    class="oc-status-chip"
                    :style="{ background: receivedStatusColor(r).bg, color: receivedStatusColor(r).color }"
                  >{{ receivedStatusLabel(r) }}</span>
                  <span v-else>—</span>
                  <div class="oc-item-sub" v-if="r.amount_received_at">{{ $t('columns.receivedDate') }}: {{ formatReceivedDate(r.amount_received_at) }}</div>
                </td>
                <td class="combined-cell-td oc-cell">
                  <div class="combined-cell">
                    <div><span class="combined-cell-label">{{ $t('columns.receivedAmount') }}:</span> {{ r.received_amount != null ? Number(r.received_amount).toFixed(2) : '—' }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.receivedDeliveryAmount') }}:</span> {{ r.received_delivery_amount != null ? formatIQD(r.received_delivery_amount) : '—' }}</div>
                  </div>
                </td>
                <td class="oc-cell ws-nowrap">
                  <span class="ovd-date-stack">
                    <span class="ovd-date-line">{{ r.created_at | moment('YYYY-MM-DD') }}</span>
                    <span class="ovd-time-line">{{ r.created_at | moment('hh:mm A') }}</span>
                  </span>
                  <div class="oc-item-sub" v-if="r.posted_at">{{ $t('delivery.postStatus') }}: {{ formatStatusDate(r.posted_at) }}</div>
                  <div class="oc-item-sub" v-if="r.sent_at">{{ $t('orders.sentStatusTitle') }}: {{ formatStatusDate(r.sent_at) }}</div>
                  <div class="oc-item-sub" v-if="r.received_at">{{ $t('delivery.completeStatus') }}: {{ formatStatusDate(r.received_at) }}</div>
                  <div class="oc-item-sub" v-if="r.canceled_at">{{ $t('orders.cancelTooltip') }}: {{ formatStatusDate(r.canceled_at) }}</div>
                  <div class="oc-item-sub" v-if="r.held_at">{{ $t('delivery.holdStatus') }}: {{ formatStatusDate(r.held_at) }}</div>
                </td>
                <td class="oc-cell">{{ r.note || '—' }}</td>
                <td class="oc-cell oc-actions-cell">
                  <div class="oc-actions-grid">
                    <a
                      :tooltip="$t('invoice.invoiceTooltip')"
                      class="oc-action-btn"
                      @click="viewRecord(r)"
                    >
                      <i
                        class="fa fa-file-text-o"
                        style="font-size: 18px; color: #666"
                      ></i>
                    </a>

                    <a
                      v-if="$_has_perm('view_delivery_invoice_shipping_invoices') && r.invoices.length"
                      :tooltip="$t('delivery.viewShippingInvoicesTooltip', { count: r.invoices.length })"
                      class="oc-action-btn"
                      @click="viewShippingInvoices(r)"
                    >
                      <i
                        class="fa fa-files-o"
                        style="font-size: 18px; color: #666"
                      ></i>
                    </a>

                    <a
                      v-if="$_has_perm('delete_delivery_invoice') && (r.status === 1 || r.status === 2)"
                      :tooltip="$t('common.delete')"
                      class="oc-action-btn"
                      @click="deleteDeliveryInvoice(r)"
                    >
                      <i
                        class="fa fa-trash red--text"
                        style="font-size: 20px"
                      />
                    </a>
                  </div>
                </td>
              </tr>
              <tr id="no-result-row">
                <td
                  :colspan="columns.length"
                  class="text-center"
                >
                  <v-alert
                    type="error"
                    :value="alertValue"
                  > {{ $t('common.noResultsFound') }} </v-alert>
                </td>
              </tr>
            </tbody>
          </datatable>
          <!-- Totals live outside the <table> on purpose: as a <tfoot>/<td colspan> they
               still fed the table's column-width algorithm and stretched the first column.
               A plain flex-wrap div below the table has zero effect on column widths -- same
               pattern as orderInvoice/orderInvoices.vue's own totals strip. -->
          <div
            v-if="footer"
            class="oc-footer-totals"
          >
            <span style="color: #616161;">{{ $t('delivery.totalRecordsFooter', { count: footer.total_count }) }}</span>
            <span style="color: #2e7d32;">{{ $t('delivery.totalPriceFooter', { amount: '$ ' + Number(footer.total_price || 0).toFixed(2) }) }}</span>
            <span style="color: #2e7d32;">{{ $t('delivery.totalDeliveryPriceFooter', { amount: Math.round(footer.total_pay_on_delivery_iqd) }) }}</span>
          </div>
          </div>

          <div class="row">
            <div class="col-xs-12 text-center oc-pagination">
              <paginate
                :page-count="pagination.lastPage"
                :page-range="2"
                :changePage="changePage"
                :container-class="'pagination'"
                :pagination="pagination"
                :page-class="'page-item'"
              >
              </paginate>
            </div>
          </div>
        </div>
      </div>
    </div>

    <view-customer-delivery-invoice
      :viewDeliveryInvoiceModal="viewDeliveryInvoiceModal"
      :invoiceData="invoiceData"
      :companyData="companyData"
      @close="viewDeliveryInvoiceModal = false"
      @detached="onInvoiceDetached"
    />

    <record-invoice-payment
      :recordPaymentModal="recordPaymentModal"
      :closeRecordPaymentModal="closeRecordPaymentModal"
      :recordPaymentData="recordPaymentData"
      :activeInvoice="activeInvoiceForPayment"
      :submitRecordPayment="submitRecordPayment"
      :loading="loadRecordPayment"
    />

    <view-attached-shipping-invoices
      :invoicesData="shippingInvoicesData"
      :companyData="companyData"
      :exRate="exRate"
      :closeModal="closeModal"
    />
  </div>
</template>

<script>
import PaginationVue from '../Pagination.vue'
import preDatatableComponent from '../preDatatableComponent.vue'
import DatatableVue from '../Datatable.vue'
import axios from 'axios'
import swal from 'sweetalert'
import ViewCustomerDeliveryInvoice from './ViewCustomerDeliveryInvoice.vue'
import RecordInvoicePayment from '../orderInvoice/RecordInvoicePayment.vue'
import ViewAttachedShippingInvoices from './ViewAttachedShippingInvoices.vue'
import toolbarButtonDisplay from '../../mixins/toolbarButtonDisplay'

export default {
  mixins: [toolbarButtonDisplay],
  components: {
    preDatatableComponent: preDatatableComponent,
    datatable: DatatableVue,
    paginate: PaginationVue,
    ViewCustomerDeliveryInvoice,
    RecordInvoicePayment,
    ViewAttachedShippingInvoices
  },
  mounted() {
    this.perPageFix()

    const token = localStorage.getItem('access_token')
    axios
      .get('api/currency-rate/latest', { headers: { Authorization: 'Bearer ' + token } })
      .then((res) => {
        this.exRate = res.data ? res.data.ex_rate || 1 : 1
      })
      .catch(() => {})
  },
  $_veeValidate: {
    validator: 'new'
  },
  data() {
    let sortOrders = {}
    // Grouped columns (redesign, mirrors allOrder.vue's pattern) -- was a flat 24-column
    // list; each grouped cell stacks several related fields (staff name into Order Info;
    // phone/phone2/customer note into Customer; total/delivery price into Pricing; the 2
    // received amount fields into their own column next to Received; the 5 lifecycle
    // timestamps + note into Date). Received status/date was split out of the main Status
    // column into its own column per request. Per-column hide no longer makes sense at this
    // granularity, so defaultHideCol is emptied and storageKey bumped (v7 -> v8) so stale
    // hidden-column selections referencing old positions aren't silently misapplied.
    let columns = [
      {
        label: '',
        text: this.$t('columns.checkBox'),
        value: '1',
        sortable: false,
        searchable: false,
        name: '',
        width: '30px'
      },
      {
        label: this.$t('columns.orderInfo'),
        text: this.$t('columns.orderInfo'),
        value: '2',
        sortable: true,
        searchable: true,
        name: 'invoice_number'
      },
      {
        label: this.$t('columns.customer'),
        text: this.$t('columns.customer'),
        value: '3',
        sortable: true,
        searchable: true,
        name: 'customer_name'
      },
      {
        label: this.$t('sidebar.invoices'),
        text: this.$t('sidebar.invoices'),
        value: '4',
        sortable: false,
        searchable: false,
        name: 'invoices',
        width: '160px'
      },
      {
        label: this.$t('columns.pricing'),
        text: this.$t('columns.pricing'),
        value: '5',
        sortable: false,
        searchable: false,
        name: 'total_price'
      },
      {
        label: this.$t('columns.statusLocation'),
        text: this.$t('columns.statusLocation'),
        value: '6',
        sortable: true,
        searchable: false,
        name: 'status_name'
      },
      {
        label: this.$t('finance.received'),
        text: this.$t('finance.received'),
        value: '7',
        sortable: false,
        searchable: false,
        name: 'received_status'
      },
      {
        label: this.$t('columns.receivedAmount'),
        text: this.$t('columns.receivedAmount'),
        value: '8',
        sortable: false,
        searchable: false,
        name: 'received_amount'
      },
      {
        label: this.$t('common.date'),
        text: this.$t('common.date'),
        value: '9',
        sortable: true,
        searchable: true,
        name: 'created_at'
      },
      {
        label: this.$t('columns.note'),
        text: this.$t('columns.note'),
        value: '10',
        sortable: true,
        searchable: true,
        name: 'note'
      },
      {
        label: this.$t('columns.actions'),
        text: this.$t('columns.actions'),
        value: '11',
        sortable: false,
        searchable: false,
        name: 'actions'
      }
    ]
    columns.forEach((column) => {
      sortOrders[column.name] = -1
    })

    return {
      url: 'api/delivery_invoice',
      alertValue: false,
      viewDeliveryInvoiceModal: false,
      invoiceData: {
        customer: {},
        orders: []
      },
      companyData: {},
      filterMade: false,
      totalResult: 0,
      prevSearchQuery: '',
      apiResults: [],
      footer: null,
      resultsVersion: 0,
      columns: columns,
      selectedIds: [],
      changeStatusDialog: false,
      changeStatusBulkMode: false,
      changeStatusRow: null,
      selectedNewStatus: null,
      changeStatusSaving: false,
      statusList: [
        { text: this.$t('finance.statusPending'), value: 1 },
        { text: this.$t('delivery.postStatus'), value: 5 },
        { text: this.$t('orders.sentStatusTitle'), value: 3 },
        { text: this.$t('delivery.completeStatus'), value: 4 },
        { text: this.$t('invoice.cancelStatus'), value: 2 },
        { text: this.$t('delivery.holdStatus'), value: 6 }
      ],
      dateFieldFilterItems: [
        { text: this.$t('columns.createdAt'), value: 'created_at' },
        { text: this.$t('delivery.completeStatus'), value: 'received_at' },
        { text: this.$t('orders.sentStatusTitle'), value: 'sent_at' },
        { text: this.$t('delivery.postStatus'), value: 'posted_at' },
        { text: this.$t('orders.cancelTooltip'), value: 'canceled_at' },
        { text: this.$t('delivery.holdStatus'), value: 'held_at' }
      ],
      // Grouped cells can't be hidden field-by-field any more -- same tradeoff allOrder.vue made.
      defaultHideCol: [],
      // Hidden by default (per-page preference, same localStorage convention as allOrder.vue).
      showFilters: localStorage.getItem('filtersVisible_deliveryInvoices') === 'true',
      recordPaymentModal: false,
      recordPaymentData: {},
      activeInvoiceForPayment: {},
      loadRecordPayment: false,
      exRate: 1,
      shippingInvoicesData: [],
      // No partial state for delivery invoices (unlike shipping invoices' 3-option version of
      // this same filter) — markReceivedAmount()/unmarkReceivedAmount() always set/clear
      // received_amount and amount_received_at together, so it's a plain received/not split.
      receivedFilterItemsOverride: [
        { text: this.$t('finance.received'), value: 'received' },
        { text: this.$t('finance.notReceived'), value: 'not_received' }
      ],
      sortKey: 'delivery_invoice_id',
      sortOrders: sortOrders,
      tableData: {
        per_page: 10,
        search: '',
        column: 'delivery_invoice_id',
        sortDir: 'desc',
        searchOneCol: '',
        searchOneVal: '',
        from_date: '',
        to_date: '',
        date_field: 'created_at',
        branches_id: ''
      },
      pagination: {
        lastPage: 1,
        currentPage: 1,
        total: 1,
        lastPageUrl: '',
        nextPageUrl: '',
        prevPageUrl: '',
        from: '',
        to: ''
      }
    }
  },

  computed: {
    // Best-effort only: counts within the currently loaded page of apiResults, not a true
    // system-wide count -- hence the "(current page)" note shown alongside it in the stat card.
    ordersTodayCount() {
      const today = new Date().toISOString().split('T')[0]
      return this.apiResults.filter(r => {
        const d = r.created_at
        return d && d.substr(0, 10) === today
      }).length
    }
  },

  methods: {
    toggleFiltersVisible() {
      this.showFilters = !this.showFilters
      localStorage.setItem('filtersVisible_deliveryInvoices', this.showFilters ? 'true' : 'false')
    },

    // Replicates preDatatableComponent.vue's own built-in Reload button (now relocated
    // into the panel-heading here) -- reaches into the child's own filter-chip/date-range
    // reset state via $refs since that state lives inside the shared component, not here.
    reloadTable() {
      this.resetGetResults()
      const preDatatable = this.$refs.preDatatable
      if (preDatatable) {
        preDatatable.filterApplied = false
        preDatatable.resetDatePickerDefaults()
        preDatatable.ware = 'Select'
      }
    },

    branchEdit(branches) {
      this.tableData.branches_id = branches.toString()
      this.getResults()
    },

    // Idempotent "set" style (rather than the toggle-diffing this used to do) — safe to call
    // repeatedly with the same currentColVal, which is required for the storageKey/
    // resultsVersion re-application in preDatatableComponent.vue to work: hidden columns need
    // to stay hidden across every data reload, not just the first time this runs.
    showHideColumn(prevColVal, currentColVal) {
      const hidden = {}
      currentColVal.forEach((v) => { hidden[v] = true })

      for (let i = 1; i <= this.columns.length; i++) {
        const selector = '#datatable td:nth-child(' + i + '),#datatable th:nth-child(' + i + ')'
        if (hidden[i]) {
          $(selector).hide()
        } else {
          $(selector).show()
        }
      }
    },

    getCompanyData(token) {
      axios
        .get('api/company_info', { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          this.companyData = response.data.data
        })
    },

    // The row is already a fully-detailed grouped record (built by DeliveryInvoiceController::
    // index()) — no separate fetch needed, it already carries every attached invoice.
    viewRecord(r) {
      let token = localStorage.getItem('access_token')
      this.getCompanyData(token)
      this.viewDeliveryInvoiceModal = true
      this.invoiceData = r
    },

    // Detaching an invoice changes which group the remaining ledger rows belong to (or removes
    // this record entirely if it was the only attached invoice) — there's no single-group refetch
    // endpoint, so close the modal and reload the list rather than show stale grouped data.
    onInvoiceDetached() {
      this.viewDeliveryInvoiceModal = false
      this.getResults()
    },

    modal(modal) {
      $('#' + modal).modal({ backdrop: 'static' })
    },

    closeModal(modal) {
      $('#' + modal).modal('toggle')
    },

    // Shows every shipping invoice attached to this delivery request stacked below each other,
    // each rendered with the exact same document/bulk-print modal shape used by orderInvoices.
    // vue's own "prepareToPrint()"/BulkPrintOrderInvoices.vue — same plain Bootstrap modal (full
    // native scrolling, Print/Close reachable in the header) rather than a v-dialog, and opened
    // only once the data has loaded, matching that same flow. The row's own r.invoices[] only
    // carries list-summary fields, not the full invoice (weight/volume/second_orders/etc.), so
    // fetch each attached invoice's full record the same way orderInvoices.vue's own
    // single-invoice view does.
    viewShippingInvoices(r) {
      const token = localStorage.getItem('access_token')
      const headers = { Authorization: 'Bearer ' + token }
      this.getCompanyData(token)

      const orderInvoiceIds = r.invoices.map(invoice => invoice.order_invoice_id_fk).filter(Boolean)
      Promise.all(orderInvoiceIds.map(id => axios.get(`api/order_invoice/${id}`, { headers })))
        .then((responses) => {
          this.shippingInvoicesData = responses.map(res => res.data)
          this.modal('view_attached_shipping_invoices_modal')
        })
        .catch((err) => {
          const msg = (err.response && err.response.data && err.response.data.message)
            ? err.response.data.message
            : this.$t('invoice.somethingWentWrong')
          swal({ text: msg, icon: 'error' })
        })
    },

    // Only Pending/Canceled rows show this icon at all (see the template's v-if), but the
    // backend enforces the same rule independently — deleting a grouped row removes the whole
    // delivery request (every attached invoice's ledger entry + the OrderDelivery itself),
    // detaching its shipping invoices without touching them (see DeliveryInvoiceController::
    // destroy()). Sending the row's own delivery_invoice_id is enough even for a grouped row —
    // the backend resolves the rest of the group from it.
    deleteDeliveryInvoice(r) {
      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('delivery.thisDeliveryInvoiceWillBeDeleted'),
        icon: 'warning',
        dangerMode: true,
        buttons: true
      }).then((willDelete) => {
        if (!willDelete) return

        let token = localStorage.getItem('access_token')
        axios
          .delete(`api/delivery_invoice/${r.delivery_invoice_id}`, { headers: { Authorization: 'Bearer ' + token } })
          .then(() => {
            this.getResults()
            swal({ title: this.$t('delivery.deletedTitle'), text: '', icon: 'success', timer: 1500, buttons: false })
          })
          .catch((err) => {
            const msg = (err.response && err.response.data && err.response.data.message)
              ? err.response.data.message
              : this.$t('invoice.somethingWentWrong')
            swal({ text: msg, icon: 'error' })
          })
      })
    },

    // A row now represents one delivery request and may carry several underlying
    // delivery_invoice_ids (one per attached shipping invoice) — selection/bulk actions operate
    // on the whole group at once, matching how Change Status already accepts an id array.
    isGroupSelected(r) {
      return r.delivery_invoice_ids.every(id => this.selectedIds.includes(id))
    },

    toggleGroupSelection(r, checked) {
      if (checked) {
        this.selectedIds = [...new Set([...this.selectedIds, ...r.delivery_invoice_ids])]
      } else {
        this.selectedIds = this.selectedIds.filter(id => !r.delivery_invoice_ids.includes(id))
      }
    },

    checkAll() {
      this.selectedIds = this.apiResults.flatMap(r => r.delivery_invoice_ids)
    },

    unCheckAll() {
      this.selectedIds = []
    },

    handleStatusFilter(val) {
      this.tableData.statuses = val || []
      this.getResults()
    },

    // Matches orderInvoices.vue's own handleReceivedFilter() — comma-joined string, read by
    // DeliveryInvoiceController::index()'s received_filter (same convention as its
    // status_filter/received_filter there, not this file's own array-based `statuses`).
    handleReceivedFilter(val) {
      this.tableData.received_filter = val && val.length > 0 ? val.join(',') : ''
      this.getResults()
    },

    // Same status values/colors as orderDeliveries.vue's statusColor(), for visual
    // consistency between the two pages.
    statusColor(status) {
      const map = {
        1: { bg: '#E0E0E0', color: '#424242' }, // Pending
        2: { bg: '#F63049', color: '#FFFFFF' }, // Cancel
        3: { bg: '#42A5F5', color: '#FFFFFF' }, // Sent
        4: { bg: '#2E7D32', color: '#FFFFFF' }, // Complete
        5: { bg: '#FFC85C', color: '#6B4C00' }, // Post
        6: { bg: '#9C27B0', color: '#FFFFFF' } // Hold
      }
      return map[status] || { bg: '#E0E0E0', color: '#424242' }
    },

    // Same badge colors as orderDeliveryInvoice.vue's/orderInvoices.vue's paymentStatusColor(),
    // for the per-invoice badges in the Invoices column.
    paymentStatusColor(status) {
      const map = {
        not_paid: { bg: '#F63049', color: '#FFFFFF' },
        partial_paid: { bg: '#FFC85C', color: '#6B4C00' },
        paid: { bg: '#89D7B7', color: '#1A5940' },
        cancel: { bg: '#FB8C00', color: '#FFFFFF' }
      }
      return map[status] || { bg: '#E0E0E0', color: '#424242' }
    },

    // Same codes/keys as orderDeliveries.vue's statusList -- status_name from the API
    // is always English, so the badge label maps the numeric code through $t() instead.
    statusLabel(status) {
      const map = {
        1: this.$t('orders.pendingStatus'),
        2: this.$t('orders.cancelTooltip'),
        3: this.$t('orders.sentStatusTitle'),
        4: this.$t('delivery.completeStatus'),
        5: this.$t('delivery.postStatus'),
        6: this.$t('delivery.holdStatus')
      }
      return map[status] || ''
    },

    // Same rounding as orderDeliveries.vue's/orderDeliveryInvoice.vue's formatIQD().
    formatIQD(value) {
      return Math.round(Number(value) || 0)
    },

    formatReceivedDate(date) {
      if (!date) return ''
      return this.$moment(date).format('YYYY-MM-DD hh:mm a')
    },

    formatStatusDate(date) {
      if (!date) return '—'
      return this.$moment(date).format('YYYY-MM-DD hh:mm a')
    },

    // Same idea as orderInvoices.vue's isFullyReceived(), extended to the two currencies this
    // page tracks — a row only counts as fully received once both the shipping-invoice total
    // (USD) and the delivery/COD fee (IQD) have been received in full.
    isFullyReceived(r) {
      if (!r.amount_received_at) return false
      const amountOk = Number(r.received_amount || 0) >= Number(r.total_price || 0)
      const deliveryOk = Number(r.received_delivery_amount || 0) >= Number(r.pay_on_delivery_iqd || 0)
      return amountOk && deliveryOk
    },

    receivedStatusColor(r) {
      return this.isFullyReceived(r)
        ? { bg: '#89D7B7', color: '#1A5940' }
        : { bg: '#FFC85C', color: '#6B4C00' }
    },

    receivedStatusLabel(r) {
      return this.isFullyReceived(r) ? this.$t('finance.received') : this.$t('delivery.partialReceivedStatus')
    },

    confirmReceiveAll() {
      if (this.selectedIds.length === 0) {
        swal({ text: this.$t('delivery.pleaseSelectAtLeastOneDeliveryInvoice'), icon: 'warning' })
        return
      }
      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('delivery.markAllSelectedInvoicesAsReceivedConfirm'),
        icon: 'warning',
        buttons: true
      }).then((willReceive) => {
        if (willReceive) this.postReceivedAmountAction('mark')
      })
    },

    confirmUnreceiveAll() {
      if (this.selectedIds.length === 0) {
        swal({ text: this.$t('delivery.pleaseSelectAtLeastOneDeliveryInvoice'), icon: 'warning' })
        return
      }
      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('delivery.unmarkAllSelectedInvoicesAsReceivedConfirm'),
        icon: 'warning',
        buttons: true
      }).then((willUnreceive) => {
        if (willUnreceive) this.postReceivedAmountAction('unmark')
      })
    },

    // A grouped row's received amounts live on its OrderDelivery, a manual row's live on this
    // ledger directly (same split as saveStatusChange()) — split the selection by row type and
    // fire whichever of the two endpoints are actually needed.
    postReceivedAmountAction(action) {
      const rows = this.apiResults.filter(r => this.isGroupSelected(r))
      const orderDeliveryIds = rows.filter(r => r.order_delivery_id).map(r => r.order_delivery_id)
      const deliveryInvoiceIds = rows.filter(r => !r.order_delivery_id).flatMap(r => r.delivery_invoice_ids)

      let token = localStorage.getItem('access_token')
      const headers = { Authorization: 'Bearer ' + token }
      const orderDeliveryUrl = action === 'mark' ? 'api/order-delivery/mark-received-amount' : 'api/order-delivery/unmark-received-amount'
      const deliveryInvoiceUrl = action === 'mark' ? 'api/delivery_invoice/mark-received-amount' : 'api/delivery_invoice/unmark-received-amount'

      const requests = []
      if (orderDeliveryIds.length) {
        requests.push(axios.post(orderDeliveryUrl, { order_delivery_ids: orderDeliveryIds }, { headers }))
      }
      if (deliveryInvoiceIds.length) {
        requests.push(axios.post(deliveryInvoiceUrl, { delivery_invoice_ids: deliveryInvoiceIds }, { headers }))
      }

      Promise.all(requests)
        .then((responses) => {
          this.unCheckAll()
          this.getResults()
          swal({ text: responses.map(res => res.data.message).join(' '), icon: 'success' })
        })
        .catch((err) => {
          const msg = (err.response && err.response.data && err.response.data.message)
            ? err.response.data.message
            : this.$t('invoice.somethingWentWrong')
          swal({ text: msg, icon: 'error' })
        })
    },

    openBulkChangeStatusDialog() {
      if (this.selectedIds.length === 0) {
        swal({ text: this.$t('delivery.pleaseSelectAtLeastOneDeliveryInvoice'), icon: 'warning' })
        return
      }
      this.changeStatusBulkMode = true
      this.changeStatusRow = null
      this.selectedNewStatus = null
      this.changeStatusDialog = true
    },

    openSingleChangeStatusDialog(r) {
      this.changeStatusBulkMode = false
      this.changeStatusRow = r
      this.selectedNewStatus = r.status
      this.changeStatusDialog = true
    },

    saveStatusChange() {
      if (!this.selectedNewStatus) return

      // A grouped row's status is the underlying delivery request's own status (see
      // DeliveryInvoiceController::index()), not this ledger's — so changing it here must call
      // orderDeliveries.vue's own change-status endpoint, not this page's, or the change would
      // silently not show up. A manual (COD) row has no delivery request and keeps using this
      // page's own endpoint. A bulk selection can contain a mix of both, so split and fire
      // whichever of the two requests are actually needed.
      const rows = this.changeStatusBulkMode
        ? this.apiResults.filter(r => this.isGroupSelected(r))
        : [this.changeStatusRow]

      const orderDeliveryIds = rows.filter(r => r.order_delivery_id).map(r => r.order_delivery_id)
      const deliveryInvoiceIds = rows.filter(r => !r.order_delivery_id).flatMap(r => r.delivery_invoice_ids)

      this.changeStatusSaving = true
      let token = localStorage.getItem('access_token')
      const headers = { Authorization: 'Bearer ' + token }
      const requests = []
      if (orderDeliveryIds.length) {
        requests.push(axios.post('api/order-delivery/change-status', {
          order_delivery_ids: orderDeliveryIds,
          status: this.selectedNewStatus
        }, { headers }))
      }
      if (deliveryInvoiceIds.length) {
        requests.push(axios.post('api/delivery_invoice/change-status', {
          delivery_invoice_ids: deliveryInvoiceIds,
          status: this.selectedNewStatus
        }, { headers }))
      }

      Promise.all(requests)
        .then((responses) => {
          this.changeStatusSaving = false
          this.changeStatusDialog = false
          if (this.changeStatusBulkMode) this.unCheckAll()
          this.getResults()
          swal({ text: responses.map(r => r.data.message).join(' '), icon: 'success', timer: 1500, buttons: false })
        })
        .catch((err) => {
          this.changeStatusSaving = false
          const msg = (err.response && err.response.data && err.response.data.message)
            ? err.response.data.message
            : this.$t('invoice.somethingWentWrong')
          swal({ text: msg, icon: 'error' })
        })
    },

    // Pays down one entry from a row's r.invoices[] (its linked shipping invoice,
    // order_invoice_id_fk) — reuses the exact same modal/endpoint as orderInvoices.vue's Record
    // Payment. A grouped row can have several attached invoices, so this pays down whichever
    // one's $ icon was clicked, not the whole group at once.
    openRecordPaymentModal(invoice, row) {
      this.activeInvoiceForPayment = {
        order_invoice_id: invoice.order_invoice_id_fk,
        customer_id_fk: row ? row.customer_id_fk : null,
        invoice_number: invoice.order_invoice_number,
        remaining_amount: invoice.order_invoice_remaining_amount
      }
      const remaining = parseFloat(invoice.order_invoice_remaining_amount)
      this.recordPaymentData = {
        amount: remaining > 0 ? remaining : null,
        note: '',
        payment_method: '',
        already_in_credit: false
      }
      this.recordPaymentModal = true
    },

    closeRecordPaymentModal() {
      this.recordPaymentModal = false
    },

    submitRecordPayment() {
      this.loadRecordPayment = true
      let token = localStorage.getItem('access_token')
      axios
        .put(`api/order_invoice/${this.activeInvoiceForPayment.order_invoice_id}`, this.recordPaymentData, {
          headers: { Authorization: 'Bearer ' + token }
        })
        .then((response) => {
          this.loadRecordPayment = false
          if (response.data.success === false) {
            swal({ text: response.data.message, icon: 'error' })
            return
          }
          this.recordPaymentModal = false
          this.getResults()
          swal({
            title: this.$t('common.done'),
            text: '',
            icon: 'success',
            timer: 1500,
            buttons: false
          })
        })
        .catch((err) => {
          this.loadRecordPayment = false
          const msg = (err.response && err.response.data && err.response.data.message)
            ? err.response.data.message
            : this.$t('invoice.somethingWentWrong')
          swal({ text: msg, icon: 'error' })
        })
    },

    getResults(url = this.url) {
      var token = localStorage.getItem('access_token')
      let theDatatable = document.querySelector('.the-datatable')
      let thePanel = document.querySelector('.panel-flat')

      // A selection made before a filter/search/reload must not silently carry over into
      // the new result set — otherwise a stale row (no longer even visible) stays selected
      // and gets included in the next bulk action, affecting rows the user never intended
      // to touch in the current (filtered) view.
      this.selectedIds = []

      if (theDatatable != null) {
        theDatatable.classList.add('slide-table-out')
        thePanel.style.overflowX = 'auto'
      }

      axios
        .get(url, { params: this.tableData, headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          let data = response.data
          this.footer = data.footer || null
          const isFlatArray = Array.isArray(data.data)
          if (isFlatArray) {
            this.filterMade = true
            this.apiResults = data.data
          } else {
            this.filterMade = false
            this.apiResults = data.data.data
          }
          this.resultsVersion++

          if (this.apiResults.length === 0) {
            this.tableData.searchOneCol = ''
            this.tableData.searchOneVal = ''
            this.tableData.search = ''
            this.alertValue = true
          } else {
            this.alertValue = false
          }
          if (isFlatArray) {
            this.removeConfigPagination()
          } else {
            this.configPagination(data.data)
          }
          theDatatable.classList.remove('slide-table-out')
          setTimeout(function () {
            thePanel.style.overflowX = 'auto'
          }, 0)
        })
        .then(() => {
          this.totalResult = 0
          for (let i = 0; i < this.apiResults.length; i++) {
            this.totalResult += 1
          }
        })
        .catch((errors) => { })
    },

    removeConfigPagination() {
      this.pagination.lastPage = ''
      this.pagination.currentPage = ''
      this.pagination.total = ''
      this.pagination.lastPageUrl = ''
      this.pagination.nextPageUrl = ''
      this.pagination.prevPageUrl = ''
      this.pagination.from = ''
      this.pagination.to = ''
    },

    resetGetResults() {
      this.tableData.searchOneCol = ''
      this.tableData.searchOneVal = ''
      this.tableData.search = ''
      this.sortKey = 'delivery_invoice_id'
      this.tableData.sortDir = 'desc'
      this.tableData.column = 'delivery_invoice_id'
      this.tableData.from_date = ''
      this.tableData.to_date = ''
      this.tableData.date_field = 'created_at'
      this.getResults()
    },

    resetGetResults2() {
      this.tableData.searchOneCol = ''
      this.tableData.searchOneVal = ''
      this.tableData.search = ''
      this.sortKey = 'delivery_invoice_id'
      this.tableData.sortDir = 'desc'
      this.tableData.column = 'delivery_invoice_id'
      this.tableData.from_date = ''
      this.tableData.to_date = ''
      this.tableData.date_field = 'created_at'
      this.getResults()
    },

    perPageFix(perPage) {
      this.tableData.per_page = perPage
      this.getResults()
    },

    changePage(pageUrl) {
      this.url = pageUrl
      this.getResults()
    },

    searchMe(str) {
      this.tableData.searchOneCol = ''
      this.tableData.searchOneVal = ''
      if (str.trim() !== '') {
        this.tableData.search = str
        this.prevSearchQuery = str
        this.getResults()
      } else {
        swal({
          text: this.$t('common.typeToSearch'),
          icon: 'warning',
          dangerMode: true
        })
        if (this.prevSearchQuery !== '') {
          this.tableData.search = ''
          this.prevSearchQuery = ''
          this.getResults()
        }
      }
    },

    sortByColumn(str) {
      this.tableData.column = str
      this.tableData.sortKey = str
      this.tableData.sortDir = this.tableData.sortDir === 'desc' ? 'asc' : 'desc'
      this.getResults()
    },

    searchOneCol(columnName, str) {
      this.tableData.searchOneCol = columnName
      this.tableData.searchOneVal = str
      this.getResults()
    },

    filterTable(fromDate, toDate, dateField) {
      this.tableData.from_date = fromDate
      this.tableData.to_date = toDate
      this.tableData.date_field = dateField || this.tableData.date_field || 'created_at'
      this.getResults()
    },

    configPagination(data) {
      this.pagination.lastPage = data.last_page
      this.pagination.currentPage = data.current_page
      this.pagination.total = data.total
      this.pagination.lastPageUrl = data.last_page_url
      this.pagination.nextPageUrl = data.next_page_url
      this.pagination.prevPageUrl = data.prev_page_url
      this.pagination.from = data.from
      this.pagination.to = data.to
    }
  }
}
</script>

<style>
@media screen and (max-width: 600px) {
  th {
    font-weight: bold;
    color: #273246;
    padding-left: 5px !important;
  }

  td {
    padding-left: 5px !important;
    padding-top: 10px !important;
    padding-bottom: 10px !important;
  }
}

.panel-heading h4 {
  text-transform: uppercase;
  margin-left: -20px;
  padding-left: 10px;
  font-weight: 500;
}

.col-xs-12 {
  padding-top: 10px;
  padding-bottom: 10px;
}
.overlay--active:before {
  opacity: 50 !important;
  background: rgba(0, 0, 0, 0.5) !important;
}

.dialog {
  box-shadow: 0 3px 5px -1px rgba(14, 14, 14, 0.212) !important;
  overflow: hidden;
  overflow-y: hidden;
}
.page-stats {
  align-items: center;
  position: relative;
  top: -8px;
  margin-right: 5px;
}

.has-error .select2-selection--single:not([class*='bg-']):not([class*='border-']) {
  border-bottom-color: red;
}

a:hover {
  color: #03a9f4;
  position: relative;
}
a[tooltip]:hover:after {
  content: attr(tooltip);
  padding: 8px 12px;
  color: #ffffff;
  position: absolute;
  right: 80%;
  bottom: 150%;
  white-space: nowrap;
  z-index: 9999;
  -moz-border-radius: 3px;
  -webkit-border-radius: 3px;
  border-radius: 3px;
  -moz-box-shadow: 0px 0px 2px #484b4e;
  -webkit-box-shadow: 0px 0px 2px #484b4e;
  box-shadow: 0px 0px 2px #484b4e;
  background-color: #484b4e;
}

@media screen and (max-width: 767px) {
  .form-group {
    margin-top: 0;
    margin-bottom: 0;
  }
}

.status-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
}

.customer-name-stack {
  display: inline-flex;
  flex-direction: column;
  vertical-align: middle;
}
.customer-phone-line {
  display: block;
  font-size: 14px;
  color: #666;
}
.combined-cell-td {
  vertical-align: middle;
}
.combined-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  font-size: 15px;
  line-height: 1.4;
  white-space: nowrap;
}
.combined-cell-label {
  color: #888;
}
.combined-cell-divider {
  width: 100%;
  border-top: 1px solid #ddd;
  margin: 3px 0;
}

/* Order IDs (r.orders) sit below the shipping invoice badges in the same cell -- fixed
   3-per-row grid (rather than each on its own line) so a delivery with many attached orders
   stays compact instead of stretching the row's height. The column itself is capped
   (columns array 'invoices' width: 160px) so a row with long order ids can't force the whole
   table wider and cause horizontal scroll. minmax(0, 1fr) let each track shrink all the way
   to 0, which combined with overflow-wrap: anywhere broke every digit onto its own line
   (looked "vertical") -- a real minimum (44px, enough for a typical order id on one line)
   keeps the id horizontal, only wrapping to a second line for the rare id too long to fit. */
.oc-order-badges {
  display: grid;
  grid-template-columns: repeat(3, minmax(44px, 1fr));
  gap: 4px 6px;
  margin-top: 4px;
}
.oc-order-badges .badge {
  margin: 0;
  white-space: normal;
  word-break: break-word;
}

/* Caps this cell at the columns array's 'invoices' width (160px) so a long shipping-invoice
   number can't force the whole table wider either -- same wrap-instead-of-clip treatment as
   the order id badges above. */
.oc-invoices-cell {
  max-width: 160px;
}
.oc-invoices-cell .badge {
  white-space: normal;
  word-break: break-word;
}

/* Totals strip -- rendered as a div AFTER </datatable>, not a <tfoot>, so it has no say in
   the table's column widths (a <td colspan> still does, and was stretching the first column).
   Same pattern as orderInvoice/orderInvoices.vue's own .oc-footer-totals. */
.oc-footer-totals {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px 20px;
  padding: 10px 12px;
  font-weight: bold;
  border-top: 1px solid #eee;
}
.oc-footer-totals > span {
  white-space: nowrap;
}
</style>

<style scoped>
/* Tighter cell padding/font-size, scoped to this page's table only (>>> reaches into
   Datatable.vue's #datatable, which this component doesn't own) — grouped columns cut down
   how much horizontal scroll a table with this many fields needs. */
>>> #datatable th,
>>> #datatable td {
  padding: 4px 6px !important;
  font-size: 12px;
}

/* preDatatableComponent.vue's .filters-no-wrap forces flex-shrink:0 on every filter field
   (elsewhere that's wanted, so it's not the shared default) — with Search/Status/Received/
   From/To Date/Show-Hide-Columns all present at once, their combined min-width no longer fits
   most viewports, so that hard floor was forcing this row into horizontal scroll instead of
   just compressing. Scoped to this page only (>>> reaches into preDatatableComponent.vue's
   own unscoped .filter-field/.filter-field-wide, which this component doesn't own) — letting
   these shrink, with a smaller floor, keeps every field on one line without needing to scroll.
*/
>>> .filters-no-wrap {
  overflow-x: visible !important;
  flex-wrap: wrap !important;
}
>>> .filters-no-wrap > .filter-field {
  flex-shrink: 1 !important;
  min-width: 96px !important;
  flex-basis: 96px !important;
}
>>> .filters-no-wrap > .filter-field-wide {
  flex-shrink: 1 !important;
  min-width: 124px !important;
  flex-basis: 124px !important;
}
</style>



// WEBPACK FOOTER //
// src/components/deliveryInvoice/deliveryInvoices.vue